import React, { useState } from 'react'
// import PropTypes from 'prop-types'
import mapStyles from '../../styles/tracker-modules/rateMap.module.less'
import { ComposableMap, Geographies, Geography } from 'react-simple-maps'
import { scaleLinear } from 'd3-scale'
import { max } from 'd3-array'
import MapLegend from './maplegend'
import ReactTooltip from 'react-tooltip'

const myRound = (max) => {
  // round up to nearest 100, 1000, 10000, or 100000
  let n = 0
  for (let i = 1; 10 ** i < max; i++) {
    n = 10 ** i
  }
  return Math.ceil(max / n) * n
}

const legendWidth = 300
// if (typeof window !== "undefined"){
//   legendWidth = window.clientWidth;
// }

const colorScale = (activeKey, val, min, max) => {
  let colors = ['#DAF2EF', '#2fa293'] // partially vaccinated (@pv), doses administered

  //set color for fully vaccinated
  if (activeKey.key === 'pvf') {
    colors = ['#D6C8E4', '#3f0676']
  }

  let innerScale = scaleLinear().domain([min, max]).range(colors)

  return innerScale(val)
}

function RateMap({
  click,
  className,
  projectionConfig,
  projectionName,
  mapSettings,
  mapName,
  style,
  mapData,
  dataLabels,
  mapKeys,
  activeArea,
  marketKey,
}) {
  let keyLabels = mapKeys.map((key) => {
    let labels = dataLabels[key]
    return {
      key: key,
      ...labels,
    }
  })

  let [activeKey, setActiveKey] = useState(keyLabels[0])
  let [minimum, setMinimum] = useState(0)
  let [tooltipContent, setTooltipContent] = useState('')

  //set max for scale and map
  let initialMax = 0
  if (activeKey.key === 'a/100k') {
    let m = max(mapData.map((d) => +d.properties[activeKey.key]))
    let rounded = myRound(m)
    initialMax = rounded
  } else {
    initialMax = 1
  }

  let [maximum, setMaximum] = useState(initialMax)

  const toggleActiveKey = (key) => {
    let newKeyObj = keyLabels.find((d) => d.key === key)
    setActiveKey(newKeyObj)

    if (activeKey.key === 'a/100k') {
      let m = max(mapData.map((d) => +d.properties[activeKey.key]))
      let rounded = myRound(m)
      setMaximum(rounded)
    } else {
      setMaximum(1)
    }

    if (typeof window.ens_specialEvent !== 'undefined') {
      window.ens_specialEvent(`Vaccine Tracker ${marketKey}`, 'Map toggle', key)
    }
  }
  return (
    <div className={className} style={style}>
      <div className={mapStyles.buttonGroup}>
        {keyLabels.length > 1 &&
          keyLabels.map((d) => {
            return (
              <button
                className={d.key !== activeKey.key ? mapStyles.unactive : ''}
                key={d.key}
                value={d.key}
                aria-pressed={d.key === activeKey.key}
                onClick={(e) => toggleActiveKey(e.target.value)}
              >
                {d.btnLabel}
              </button>
            )
          })}
      </div>

      <MapLegend
        width={legendWidth}
        label={activeKey.label}
        activeKey={activeKey}
        colorScale={colorScale}
        max={maximum}
        min={minimum}
      />

      <ReactTooltip className={mapStyles.tooltip} type="light" html={true}>
        {tooltipContent}
      </ReactTooltip>

      <ComposableMap
        projectionConfig={projectionConfig}
        projection={projectionName}
        width={mapSettings.width}
        height={mapSettings.height}
        style={{
          width: '100%',
          height: 'auto',
        }}
        data-tip=""
      >
        {/* base fill */}
        <Geographies geography={mapData} disableOptimization>
          {(data, projection) => {
            return data.geographies.map((geography, i) => {
              // first sets color of fill, then set opacity for different states. Don't want current selected county to look clickable
              // [fillColor, opacity, ]
              const changingStyle = [
                colorScale(
                  activeKey,
                  geography.properties[activeKey.key],
                  minimum,
                  maximum
                ),
                0.5,
              ]
              return (
                <Geography
                  className={className}
                  key={`path-${i}`}
                  cacheId={`${mapName}-path-${i}-${changingStyle}`}
                  geography={geography}
                  projection={projection}
                  onClick={(e) => click(geography.properties.n, 'map click')}
                  onMouseEnter={() => {
                    const { n: name, r: region } = geography.properties
                    setTooltipContent(
                      `<b>${name} ${
                        marketKey !== 'CT' ? `County` : ''
                      }</b> <br/> ${
                        region !== undefined ? `Region ${region}` : ''
                      }`
                    )
                  }}
                  onMouseLeave={() => {
                    setTooltipContent('')
                  }}
                  style={{
                    default: {
                      stroke: '#fff',
                      strokeWidth: 1,
                      outline: 'none',
                      fill: changingStyle[0],
                    },
                    hover: {
                      stroke: '#fff',
                      opacity: changingStyle[1],
                      strokeWidth: 1,
                      outline: 'none',
                      fill: changingStyle[0],
                      cursor: 'pointer',
                    },
                    pressed: {
                      opacity: '#EE3C52',
                      stroke: 'none',
                      strokeWidth: 3,
                      outline: 'none',
                      fill: changingStyle[0],
                    },
                  }}
                />
              )
            })
          }}
        </Geographies>
        {/* active outlines */}
        <Geographies geography={mapData} disableOptimization>
          {(data, projection) => {
            return data.geographies.map((geography, i) => {
              // first sets color of stroke, then set opacity, then stroke width for different states.
              //Don't want current selected county to look clickable
              const changingStyle =
                (geography.properties.GEOID &&
                  String(geography.properties.GEOID) ===
                    String(activeArea.properties.GEOID)) ||
                (marketKey === 'Albany' &&
                  geography.properties.r === activeArea.properties.r)
                  ? ['black', 1, 3]
                  : ['none', 0, 0]
              return (
                <Geography
                  className={className}
                  key={`path-${i}`}
                  cacheId={`${mapName}-outline-${i}-${changingStyle}`}
                  geography={geography}
                  projection={projection}
                  style={{
                    default: {
                      stroke: changingStyle[0],
                      strokeOpacity: changingStyle[1],
                      strokeWidth: changingStyle[2],
                      outline: 'none',
                      fill: 'none',
                    },
                    hover: {
                      stroke: changingStyle[0],
                      strokeOpacity: changingStyle[1],
                      strokeWidth: changingStyle[2],
                      outline: 'none',
                      fill: 'none',
                      cursor: 'pointer',
                    },
                    pressed: {
                      stroke: changingStyle[0],
                      strokeOpacity: changingStyle[1],
                      strokeWidth: changingStyle[2],
                      outline: 'none',
                      fill: 'none',
                      cursor: 'pointer',
                    },
                  }}
                />
              )
            })
          }}
        </Geographies>
      </ComposableMap>
    </div>
  )
}

// RateMap.propTypes = {}

export default React.memo(RateMap)
